import setGlobalState from '../../../redux/setGlobalState';
import store from '../../../store';
import {
    getDiscriminatorIntent,
    getEffectiveSessionIntent,
    getFormIdForIntent,
    getPayloadIntent,
} from './agentSchema';
import {sameFieldValues} from './agentObjectUtils';
import {resolveAskOptionValue} from './askOptionAnswer';
import {resetAgentEventState} from './resetAgentEventState';
import type {
    AiAgentExecuteResult,
    AiAgentFormId,
    AiAgentPendingProposal,
    AiAgentUiAction,
    AiAgentUiActionPayload,
} from '../../../redux/aiAgent/aiAgent.type';
import type {ChatAskPrompt} from '../../aiMessageGenerator/utils/types';

export function rememberDiscriminatorFromAsk(value: string | undefined, pendingAsk: ChatAskPrompt | null): void {
    const discriminatorIntent = getDiscriminatorIntent();
    if(!discriminatorIntent || !value || !pendingAsk?.options?.length) {
        return;
    }
    const locked = getEffectiveSessionIntent();
    if(locked && locked !== discriminatorIntent) {
        return;
    }
    const optionValue = resolveAskOptionValue(value, pendingAsk);
    if(optionValue) {
        setGlobalState('aiAgent.sessionDiscriminator', optionValue);
    }
}

/**
 * The values held are read as the open form's, so a turn that moved to another form must not
 * take the previous record's values with it: both forms have a `title`, and left behind it is
 * sent as this record's "already filled".
 */
function rememberActiveForm(formId: AiAgentFormId | null | undefined): void {
    if(!formId) {
        return;
    }
    if(store.getState().aiAgent?.activeFormId !== formId) {
        setGlobalState('aiAgent.currentFormValues', null);
    }
    setGlobalState('aiAgent.activeFormId', formId);
}

export function rememberProposalContext(proposal: AiAgentPendingProposal): void {
    // eventType is the discriminator's field on the wire — its first implementation.
    if(proposal.eventType) {
        setGlobalState('aiAgent.sessionDiscriminator', proposal.eventType);
    }
    setGlobalState('aiAgent.sessionIntent', proposal.intent);
    rememberActiveForm(proposal.formId);
}

export function rememberUiActionContext(uiAction: AiAgentUiAction): void {
    const payload = uiAction.payload;
    if(payload?.eventType) {
        setGlobalState('aiAgent.sessionDiscriminator', payload.eventType);
    }
    const intent = getPayloadIntent(payload);
    const locked = getEffectiveSessionIntent();
    if(intent && (!locked || locked === intent)) {
        setGlobalState('aiAgent.sessionIntent', intent);
    }
    // Last: the lock above is read against the form of the turn before this one.
    rememberActiveForm(payload?.formId ?? getFormIdForIntent(intent));
}

/**
 * The record the chat was collecting exists, so the flow locked to one intent is over. Left
 * locked, the server keeps answering every later request with that intent alone — a ticket
 * asked for right after the event was created comes back as "here is what I can do".
 * activeFormId and currentFormValues stay: a change to the created record is rebuilt from them.
 */
export function releaseSessionFlow(): void {
    setGlobalState('aiAgent.sessionIntent', null);
    // The created event may not be of the type that was collected ("scheduled" is created as a
    // ticket event), and inside the event the type is read from the event itself.
    setGlobalState('aiAgent.sessionDiscriminator', null);
}

export function rememberCreatedDraft(fields: Record<string, unknown>, eid: number | null = null): void {
    setGlobalState('aiAgent.createdDraftFields', fields);
    setGlobalState('aiAgent.createdDraftEid', eid);
    // The next turn's change is merged onto these, so a one-field request still carries a
    // complete record.
    setGlobalState('aiAgent.currentFormValues', fields);
}

/** The record execute just created or changed — the "go to page" button under the chat opens it. */
export function rememberCreatedRecord(result: AiAgentExecuteResult): void {
    setGlobalState('aiAgent.createdRecord', result);
}

/** The server marked this turn as another event, not more fields on the current one. */
export function isNewEventRequest(payload: AiAgentUiActionPayload | undefined): boolean {
    return payload?.newEvent === true;
}

export function isIntentSwitch(
    source: AiAgentUiActionPayload | AiAgentPendingProposal | undefined | null,
): boolean {
    return source?.switchIntent === true;
}

/**
 * The producer left the record the chat was collecting for another action. The form it belonged
 * to goes with the lock: kept, its values would be sent as the new record's "already filled".
 */
export function switchSessionFlow(): void {
    releaseSessionFlow();
    setGlobalState('aiAgent.activeFormId', null);
    resetAgentEventState();
}

/**
 * The producer asked for a change to a record the chat already created. There is no edit
 * action, so the change becomes a new draft built from every value known plus the change —
 * which also means a turn that only repeats the created values must create nothing.
 */
export function isRepeatOfCreatedDraft(proposal: AiAgentPendingProposal): boolean {
    const created = store.getState().aiAgent?.createdDraftFields;
    return !!created && sameFieldValues(created, proposal.fields);
}
